import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, DataTable, StatusBadge, Sel, Field, opts } from "@/components/kit";
import { api, useApi, fmtErr, dt, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATS = ["whitelist", "blacklist", "gray", "vip", "tenant", "staff", "repeat_violator"];

function AddWatch({ onClose, onSaved }) {
  const [f, setF] = useState({ plate: "", category: "gray", reason: "", vehicle_details: "" });
  const save = async () => {
    try {
      await api.post("/watchlist", f);
      toast.success("Watchlist updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card" data-testid="watchlist-dialog">
        <DialogHeader><DialogTitle>Add to watchlist</DialogTitle></DialogHeader>
        <Field label="Plate"><Input data-testid="watchlist-plate" className="font-mono uppercase" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} /></Field>
        <Field label="Category"><Sel value={f.category} onChange={(v) => setF({ ...f, category: v })} options={opts(CATS)} testid="watchlist-category" className="w-full" /></Field>
        <Field label="Reason"><Input data-testid="watchlist-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
        <Field label="Vehicle details"><Input data-testid="watchlist-details" value={f.vehicle_details} onChange={(e) => setF({ ...f, vehicle_details: e.target.value })} placeholder="Color, make, model" /></Field>
        <Button data-testid="watchlist-save" disabled={!f.plate} onClick={save}>Save</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Watchlist() {
  const { can, branchName } = useAuth();
  const [cat, setCat] = useState("");
  const [add, setAdd] = useState(false);
  const { data, loading, reload } = useApi("/watchlist", { category: cat });
  const alerts = useApi("/watchlist/alerts", {});
  const act = async (fn, msg, r) => {
    try {
      await fn();
      toast.success(msg);
      r();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const unack = (alerts.data || []).filter((a) => !a.acknowledged).length;
  return (
    <div className="fade-up">
      <PageHeader title="Vehicle Watchlist" subtitle="Flagged vehicles trigger alerts at entry/exit; blacklisted vehicles are denied" testid="watchlist-header">
        {can("add", "edit") && <Button data-testid="watchlist-add-btn" onClick={() => setAdd(true)}><Plus className="h-4 w-4" /> Add plate</Button>}
      </PageHeader>
      <Tabs defaultValue="alerts">
        <TabsList>
          <TabsTrigger value="alerts" data-testid="tab-watch-alerts">Alerts {unack > 0 && <span className="ml-1.5 rounded bg-red-500 px-1.5 font-mono text-[10px] text-white">{unack}</span>}</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-watch-list">Watchlist</TabsTrigger>
        </TabsList>
        <TabsContent value="alerts" className="mt-4">
          <DataTable testid="watch-alerts-table" loading={alerts.loading} rows={alerts.data} columns={[
            { key: "at", label: "Time", render: (r) => dt(r.at) },
            { key: "plate", label: "Plate", render: (r) => <span className="plate text-xs">{r.plate}</span> },
            { key: "category", label: "Category", render: (r) => <StatusBadge status={r.category} /> },
            { key: "b", label: "Branch / lane", render: (r) => `${branchName(r.branch_id)} • ${r.lane_name || "—"} (${r.direction})` },
            { key: "reason", label: "Details", render: (r) => <span className="text-xs">{r.reason}</span> },
            { key: "ack", label: "Acknowledgment", render: (r) => r.acknowledged ? <span className="text-xs">{r.acknowledged_by} • {dt(r.acknowledged_at)}</span> :
              <Button size="sm" variant="outline" data-testid={`watch-ack-${r.id}`} onClick={() => act(() => api.post(`/watchlist/alerts/${r.id}/ack`), "Acknowledged", alerts.reload)}>Acknowledge</Button> },
          ]} />
        </TabsContent>
        <TabsContent value="list" className="mt-4">
          <div className="mb-3"><Sel value={cat} onChange={setCat} testid="watchlist-category-filter" allLabel="All categories" options={opts(CATS)} /></div>
          <DataTable testid="watchlist-table" loading={loading} rows={data} columns={[
            { key: "plate", label: "Plate", render: (r) => <span className="plate text-xs">{r.plate}</span> },
            { key: "category", label: "Category", render: (r) => <StatusBadge status={r.category} /> },
            { key: "reason", label: "Reason" },
            { key: "vehicle_details", label: "Vehicle" },
            { key: "active", label: "Active", render: (r) => (r.active ? "Yes" : "No") },
            { key: "created_by", label: "Added by" },
            { key: "updated_at", label: "Updated", render: (r) => dt(r.updated_at || r.created_at) },
            { key: "act", label: "", render: (r) => r.active && can("edit") && <button className="text-xs text-red-500 underline" data-testid={`watch-deactivate-${r.plate}`} onClick={() => act(() => api.delete(`/watchlist/${r.id}`), "Deactivated", reload)}>Deactivate</button> },
          ]} />
        </TabsContent>
      </Tabs>
      {add && <AddWatch onClose={() => setAdd(false)} onSaved={reload} />}
      <span className="hidden">{label("x")}</span>
    </div>
  );
}
